import React, { useRef, useState, useEffect } from 'react'
import './cart-page.css'
import MainContainer from "../../components/container/MainContainer"; import Breadcrumb from '../../components/breadcrumb/Breadcrumb'
import BreadcrumbData from '../../utils/constants/BreadcrumbData.constants'
import Card from '../../components/Cards/Card'
import CustomButton from '../../components/CustomButton/CustomButton'
import ModalWrapper from '../../components/Modal/ModalWrapper'
import InputField from '../../components/InputField/InputField'
import { SubmitEvent } from '../../utils/constants/Types.constants'
import setInputValues from '../../utils/helpers/setInputValue'
import { useAppDispatch, useAppSelector } from '../../store/storeRequests'
import StoreSelectors from '../../utils/constants/StoreSelectors.constants'
import { useNavigate } from 'react-router-dom'
import { getDetails, postDetails } from '../../services/http/http.service'
import { APIResponseCodes } from '../../utils/constants/keys.constants'
import { setCart } from '../../store/selectedAPIs/selectedAPIs.slice'
import APIList from '../../utils/constants/APIList'
import { hideLoader, showLoader } from '../../store/Loader/loader.slice'
import { showMessageOnFailure } from '../../store/error.actions'
import { setUATOrders, setUserCartItems } from '../../store/authentication/authentication.slice'
import encrypt from '../../utils/helpers/encrypt'
import wishlistIcon from '../../assets/wishlist-blue.svg'
import { NavList } from '../../utils/constants/navigation';
import { message } from '../../utils/constants/Messages';

const CartPage: React.FunctionComponent = () => {

  const navigate = useNavigate()
  const modalWrapperRef = useRef()
  const isMounted = useRef(false)
  const dispatch = useAppDispatch()

  const { user, uatOrders, primaryUser } = useAppSelector(StoreSelectors.USER_STATE)
  
  const { cart } = useAppSelector(StoreSelectors.SELECTED_STATE)

  const [codeDetails, setCodeDetails] = useState({ SEBI: '', RBI: '', GSTIN: '' })
  const [billingDetails, setBillingDetails] = useState({
    pan: '',
    gstin: '',
    arn: ''
  })

  const getAllCartItems = async () => {
    const response = await getDetails(APIList.GET_CART_ITEMS, getAllCartItems)
    if (response?.statusCode === APIResponseCodes.SUCCESS) {
      dispatch(setCart(response.data))
    }
  }

  useEffect(() => {
    if (isMounted.current === false) {
      getAllCartItems()
    }
    isMounted.current = false
  }, [])

  // keep codeDetails in sync when billingDetails are loaded
  useEffect(() => {
    if (billingDetails?.arn || billingDetails?.pan || billingDetails?.gstin) {
      setCodeDetails({
        SEBI: billingDetails.arn || '',
        RBI: billingDetails.pan || '',
        GSTIN: billingDetails.gstin || ''
      })
    }
  }, [billingDetails])

  /** On success of validating arn/rbi credentials, this function will call order API to place an order */
  const placeOrder = async (payload: { arn_code: string, ria_code: string, gstin: string }) => {
    dispatch(showLoader(message.PLACE_ORDER))
    const response = await postDetails(APIList.POST_ORDER, payload, () => placeOrder(payload))

    if (response.statusCode === APIResponseCodes.SUCCESS) {
      if (cart.length !== 0) {
        const temp: Array<number> = []
        cart.forEach((item: any) => temp.push(item.group_version_id))
        dispatch(setUATOrders([...uatOrders, ...temp]))
      }
      dispatch(setCart([]))
      dispatch(setUserCartItems([]))
      navigate(`${NavList.ORDER_DETAILS}${encodeURIComponent(encrypt(response.data.orderId))}`)
    }
    dispatch(hideLoader())
  }
    
  /** On ARN/RIA code submission  */
  const handleSubmit = async (ev: SubmitEvent) => {
    ev.preventDefault()
    dispatch(showLoader(message.VALIDATE))
    const reqPayload = {
      arn_code: user.registered_with_sebi ? codeDetails.SEBI.trim() : '',
      ria_code: (user.registered_with_sebi || user.registered_with_rbi||user.technical_service_provider) ?  codeDetails.RBI.trim() : '',
      gstin: (user.registered_with_sebi ||user.registered_with_rbi||user.technical_service_provider) ?  codeDetails.GSTIN.trim() : ''
    }
    const response = await postDetails(APIList.VALIDATE_CODES, reqPayload, () => handleSubmit(ev))

    if (response.statusCode === APIResponseCodes.SUCCESS) {
      response.data.validate ? placeOrder(reqPayload) : dispatch(showMessageOnFailure(message.INCORRECT_INPUT))
    }

    dispatch(hideLoader())
  }

  /** Proceed cart items to checkout */
  const proceedToCheckout = async() => {
    if (user.registered_with_sebi || user.registered_with_rbi || user.technical_service_provider) {
      if (typeof modalWrapperRef?.current !== 'undefined') {
        let openModal: { toggleModalWrapper: Function } = modalWrapperRef.current
        openModal.toggleModalWrapper(true)
      }
    } else {
      placeOrder({ arn_code: '', ria_code: '', gstin: '' })
    }
    try {
      const response = await getDetails(APIList.USER_BILLING_DETAILS, proceedToCheckout)
      dispatch(hideLoader())
      if (response.statusCode === APIResponseCodes.SUCCESS) {
        // set billingDetails AND also copy them into codeDetails so inputs show them by default
        setBillingDetails({
          gstin: response.data.gstin,
          pan: response.data.pan,
          arn: response.data.arn_code
        })

        setCodeDetails({
          SEBI: response.data.arn_code || '',
          RBI: response.data.pan || '',
          GSTIN: response.data.gstin || ''
        })
      }
    } catch (error) {
      dispatch(hideLoader())
      console.log(error)
    }
  }

  // helper: disable inputs only when billing details exist AND the logged-in user is NOT the primary user
  const inputDisabledWhenNotPrimary = (fieldValue?: string) => {
    return !!fieldValue && !primaryUser
  }

  return (
    <MainContainer>
      <Breadcrumb routes={BreadcrumbData.cart} />
      <div className="d-flex align-items-center justify-content-between cart-active-header">
        <h2 className='text-capitalize'>My Cart</h2>
        <button onClick={() => navigate(NavList.WISHLIST)} className="border-0 cart-to-wishlist-btn d-flex align-items-center">
          <img width={16} height={16} src={wishlistIcon} alt="wishlist" />
          <p className="mb-0 font-16 weight-500 ms-2">Wishlists</p>
        </button>
      </div>
      {
        cart.length !== 0 ?
          <Card from='cart' list={cart} />
          : <p className="mb-0 font-14 weight-500 text-center primary-text-black">Your cart is empty!!!</p>
      }
      <ModalWrapper ref={modalWrapperRef} title='Compliance Details' callback={() => setCodeDetails({ SEBI: '', RBI: '', GSTIN: '' })}>
        <form onSubmit={handleSubmit}>
          {
            user.registered_with_sebi &&
            <InputField
              name='SEBI'
              type='text'
              // bind to codeDetails so changes are reflected
              value={codeDetails.SEBI}
              placeholder='ARN/RIA/EOP Code'
              // disable only when billing exists and current user is not primary
              disabled={inputDisabledWhenNotPrimary(billingDetails?.arn)}
              underlinedInput={true}
              minLength={7}
              maxLength={13}
              // pattern="^[Aa][Rr][Nn]-[0-9]{4,6}$"
              pattern="^(?:[Aa][Rr][Nn]|[Ii][Nn][Aa]|[Ee][Oo][Pp])-?[0-9]+$"
              required
              onChangeInput={(ev) => setInputValues(ev, codeDetails, setCodeDetails)}
            />
          }
          {
            (user.registered_with_sebi || user.registered_with_rbi || user.technical_service_provider) &&
            <InputField
              className='mt-4'
              name='RBI'
              type='text'
              value={codeDetails.RBI}
              placeholder='PAN'
              disabled={inputDisabledWhenNotPrimary(billingDetails?.pan)}
              underlinedInput={true}
              minLength={10}
              maxLength={10}
              pattern='^[A-Z]{5}[0-9]{4}[A-Z]$'
              required
              onChangeInput={(ev) => setInputValues(ev, codeDetails, setCodeDetails)}
            />
          }
          {
            (user.registered_with_sebi || user.registered_with_rbi || user.technical_service_provider) &&
            <InputField
              className='mt-4'
              name='GSTIN'
              type='text'
              value={codeDetails.GSTIN}
              placeholder='GSTIN'
              disabled={inputDisabledWhenNotPrimary(billingDetails?.gstin)}
              underlinedInput={true}
              minLength={15}
              maxLength={15}
              pattern='^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$'
              // required
              onChangeInput={(ev) => setInputValues(ev, codeDetails, setCodeDetails)}
              showErrors={false}
            />
          }
          <div className="mt-4 mb-2">
            <CustomButton type='submit' size='md'>Continue</CustomButton>
          </div>
        </form>
      </ModalWrapper>
      {
        cart.length !== 0 &&
        <div className="cart-checkout-btn">
          <CustomButton type='button' size='md' onClick={proceedToCheckout}> Proceed to Checkout </CustomButton>
        </div>
      }
    </MainContainer>
  )
}

export default CartPage
